import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { BetaBadge, Button, DialogView, Loader, Notification, PageView, PageTopBar } from "../../../components"
import { PrinterIcon, PlusIcon } from "@heroicons/react/outline"
import {
    fetchContainerBarcodes,
    generateContainerBarcodeBatch,
    downloadContainerBarcodeLabel,
    downloadContainerBarcodesBulkLabels
} from "../../../services"
import { fetchStores } from "../../../services/storeService"

const CONTAINER_TYPES = ["GROUP_CONTAINER", "PRODUCT_CONTAINER"]
const QUICK_QUANTITIES = [10, 50, 100]
const MAX_BULK_PRINT = 100

export const BarcodePoolContainer = () => {
    const { i18n } = useTranslation()
    const { warehouse_id } = useParams()
    const queryClient = useQueryClient()

    const [containerType, setContainerType] = useState("GROUP_CONTAINER")
    const [availableFilter, setAvailableFilter] = useState("available") // available | in_use | all
    const [showStoreScoped, setShowStoreScoped] = useState(false)
    const [storeId, setStoreId] = useState(null)
    const [selectedIds, setSelectedIds] = useState([])
    const [generateOpen, setGenerateOpen] = useState(false)
    const [generateForm, setGenerateForm] = useState({ container_type: "GROUP_CONTAINER", quantity: 50 })
    const [notification, setNotification] = useState(null)
    const [downloading, setDownloading] = useState(false)

    const queryParams = useMemo(() => {
        const params = {
            warehouse_id,
            container_type: containerType,
            scope: showStoreScoped ? "store_only" : "warehouse_only"
        }
        if (availableFilter === "available") params.available = true
        if (availableFilter === "in_use") params.available = false
        if (showStoreScoped && storeId) params.store_id = storeId
        return params
    }, [warehouse_id, containerType, availableFilter, showStoreScoped, storeId])

    // Reset the selection whenever the visible list changes (any filter knob)
    // so the "Imprimir selección" button never points at off-screen rows.
    useEffect(() => {
        setSelectedIds([])
    }, [queryParams])

    const enabled = !showStoreScoped || !!storeId

    const { data: barcodesData, isLoading, isFetching } = useQuery(
        ["container_barcodes", queryParams],
        () => fetchContainerBarcodes(queryParams),
        { enabled, keepPreviousData: true, refetchOnWindowFocus: false }
    )

    const { data: storesData } = useQuery(
        ["stores", warehouse_id],
        () => fetchStores({ warehouse_id }),
        { refetchOnWindowFocus: false, enabled: showStoreScoped }
    )

    // fetchStores returns { stores: [...] } but tolerate a bare array too.
    // Mirrors the cascade TemplatesListContainer uses.
    const stores = useMemo(
        () => storesData?.stores || (Array.isArray(storesData) ? storesData : []),
        [storesData]
    )

    const barcodes = useMemo(() => Array.isArray(barcodesData) ? barcodesData : (barcodesData?.barcodes || []), [barcodesData])

    const generateMutation = useMutation(
        (data) => generateContainerBarcodeBatch(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["container_barcodes"])
                setGenerateOpen(false)
                setNotification({ type: "success", message: i18n.t("barcode_pool.notifications.generated", { count: generateForm.quantity }) })
            },
            onError: (error) => {
                setNotification({ type: "error", message: error?.response?.data?.error || i18n.t("barcode_pool.notifications.generate_error") })
            }
        }
    )

    const handleToggleSelection = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }

    const handleSelectAll = () => {
        const visibleIds = barcodes.map(b => b.id)
        const allSelected = visibleIds.every(id => selectedIds.includes(id))
        setSelectedIds(allSelected ? [] : visibleIds)
    }

    const handlePrintOne = async (id) => {
        setDownloading(true)
        try {
            await downloadContainerBarcodeLabel(id)
        } catch (e) {
            setNotification({ type: "error", message: i18n.t("barcode_pool.notifications.print_error") })
        }
        setDownloading(false)
    }

    const handlePrintBulk = async () => {
        if (selectedIds.length === 0) return
        if (selectedIds.length > MAX_BULK_PRINT) {
            setNotification({ type: "error", message: i18n.t("barcode_pool.notifications.too_many", { max: MAX_BULK_PRINT }) })
            return
        }
        setDownloading(true)
        try {
            await downloadContainerBarcodesBulkLabels(selectedIds)
            setSelectedIds([])
        } catch (e) {
            setNotification({ type: "error", message: i18n.t("barcode_pool.notifications.print_error") })
        }
        setDownloading(false)
    }

    const handleGenerate = () => {
        const payload = {
            warehouse_id: parseInt(warehouse_id, 10),
            container_type: generateForm.container_type,
            quantity: parseInt(generateForm.quantity, 10)
        }
        if (showStoreScoped && storeId) payload.store_id = storeId
        generateMutation.mutate(payload)
    }

    const formatContainerType = (t) => i18n.t(`barcode_pool.container_types.${t}`)
    const formatStatus = (b) => b.container_id == null
        ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">{i18n.t("barcode_pool.statuses.available")}</span>
        : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{i18n.t("barcode_pool.statuses.in_use")}</span>

    const allVisibleSelected = barcodes.length > 0 && barcodes.every(b => selectedIds.includes(b.id))
    const someVisibleSelected = barcodes.some(b => selectedIds.includes(b.id)) && !allVisibleSelected

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="text-lg font-semibold flex items-center gap-2">
                        {i18n.t("barcode_pool.title")}
                        <BetaBadge />
                    </div>
                    <div className="flex gap-2 ml-auto">
                        <Button type="secondary" onClick={handlePrintBulk} disabled={selectedIds.length === 0 || downloading}>
                            <PrinterIcon className="h-4 w-4 inline mr-1" />
                            {i18n.t("barcode_pool.actions.print_selection", { count: selectedIds.length })}
                        </Button>
                        <Button onClick={() => { setGenerateForm({ container_type: containerType, quantity: 50 }); setGenerateOpen(true) }}>
                            <PlusIcon className="h-4 w-4 inline mr-1" />
                            {i18n.t("barcode_pool.actions.generate_batch")}
                        </Button>
                    </div>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            <Loader show={isLoading || downloading} />
            <p className="text-sm text-gray-500 mb-4">{i18n.t("barcode_pool.subtitle")}</p>

            <div className="flex flex-wrap gap-4 mb-4 p-3 bg-gray-50 rounded">
                <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">{i18n.t("barcode_pool.filters.container_type")}</label>
                    <div className="flex gap-1">
                        {CONTAINER_TYPES.map(t => (
                            <button
                                key={t}
                                onClick={() => setContainerType(t)}
                                className={`px-3 py-1 rounded text-sm ${containerType === t ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                            >
                                {formatContainerType(t)}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">{i18n.t("barcode_pool.filters.status")}</label>
                    <div className="flex gap-1">
                        {[
                            { id: "available", label: i18n.t("barcode_pool.statuses.available") },
                            { id: "in_use", label: i18n.t("barcode_pool.statuses.in_use") },
                            { id: "all", label: i18n.t("barcode_pool.filters.all") }
                        ].map(opt => (
                            <button
                                key={opt.id}
                                onClick={() => setAvailableFilter(opt.id)}
                                className={`px-3 py-1 rounded text-sm ${availableFilter === opt.id ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="ml-auto flex items-end gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={showStoreScoped}
                            onChange={(e) => { setShowStoreScoped(e.target.checked); setStoreId(null) }}
                            className="rounded"
                        />
                        {i18n.t("barcode_pool.filters.show_store_scoped")}
                    </label>
                    {showStoreScoped && (
                        <select
                            value={storeId || ""}
                            onChange={(e) => setStoreId(e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                            <option value="">{i18n.t("barcode_pool.filters.select_store")}</option>
                            {stores.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {showStoreScoped && !storeId && (
                <div className="p-6 text-center text-gray-500 bg-white border rounded">
                    {i18n.t("barcode_pool.filters.select_store_hint")}
                </div>
            )}

            {enabled && (
                <div className="bg-white border rounded overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        ref={el => { if (el) el.indeterminate = someVisibleSelected }}
                                        onChange={handleSelectAll}
                                        disabled={barcodes.length === 0}
                                    />
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("barcode_pool.columns.barcode")}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("barcode_pool.columns.type")}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("barcode_pool.columns.status")}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("barcode_pool.columns.created_at")}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("barcode_pool.columns.container")}</th>
                                <th className="px-4 py-2"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {barcodes.length === 0 && !isFetching && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">
                                        {i18n.t("barcode_pool.empty")}
                                    </td>
                                </tr>
                            )}
                            {barcodes.map((b) => (
                                <tr key={b.id} className={selectedIds.includes(b.id) ? "bg-blue-50" : ""}>
                                    <td className="px-4 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(b.id)}
                                            onChange={() => handleToggleSelection(b.id)}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm font-mono">{b.barcode}</td>
                                    <td className="px-4 py-2 text-sm">{formatContainerType(b.container_type)}</td>
                                    <td className="px-4 py-2">{formatStatus(b)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{b.container_id ? `#${b.container_id}` : "—"}</td>
                                    <td className="px-4 py-2 text-right">
                                        <button
                                            onClick={() => handlePrintOne(b.id)}
                                            className="text-indigo-600 hover:text-indigo-900 text-sm"
                                            disabled={downloading}
                                        >
                                            <PrinterIcon className="h-4 w-4 inline" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500 flex justify-between">
                        <span>{i18n.t("barcode_pool.total", { count: barcodes.length })}</span>
                        {selectedIds.length > 0 && (
                            <span>{i18n.t("barcode_pool.selected", { count: selectedIds.length })}</span>
                        )}
                    </div>
                </div>
            )}

            <DialogView open={generateOpen} setOpen={setGenerateOpen}>
                <h3 className="text-lg font-medium text-gray-900 mb-1">{i18n.t("barcode_pool.generate_dialog.title")}</h3>
                <p className="text-sm text-gray-500 mb-4">{i18n.t("barcode_pool.generate_dialog.subtitle")}</p>

                <div className="mb-3">
                    <label className="text-xs font-medium text-gray-700 block mb-1">{i18n.t("barcode_pool.filters.container_type")}</label>
                    <div className="flex gap-1">
                        {CONTAINER_TYPES.map(t => (
                            <button
                                key={t}
                                onClick={() => setGenerateForm(prev => ({ ...prev, container_type: t }))}
                                className={`px-3 py-1 rounded text-sm ${generateForm.container_type === t ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                            >
                                {formatContainerType(t)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="text-xs font-medium text-gray-700 block mb-1">{i18n.t("barcode_pool.generate_dialog.quantity")}</label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            max={500}
                            value={generateForm.quantity}
                            onChange={(e) => setGenerateForm(prev => ({ ...prev, quantity: e.target.value }))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
                        />
                        <div className="flex gap-1">
                            {QUICK_QUANTITIES.map(q => (
                                <button
                                    key={q}
                                    onClick={() => setGenerateForm(prev => ({ ...prev, quantity: q }))}
                                    className="px-2 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200"
                                >
                                    +{q}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {showStoreScoped && storeId && (
                    <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        {i18n.t("barcode_pool.generate_dialog.store_scoped_warning", {
                            store: stores.find(s => s.id === storeId)?.name || `#${storeId}`
                        })}
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    <Button type="secondary" onClick={() => setGenerateOpen(false)} disabled={generateMutation.isLoading}>
                        {i18n.t("barcode_pool.generate_dialog.cancel")}
                    </Button>
                    <Button
                        onClick={handleGenerate}
                        disabled={generateMutation.isLoading || !Number.isInteger(Number(generateForm.quantity)) || Number(generateForm.quantity) < 1}
                    >
                        {generateMutation.isLoading ? i18n.t("barcode_pool.generate_dialog.generating") : i18n.t("barcode_pool.generate_dialog.confirm")}
                    </Button>
                </div>
            </DialogView>

            <Notification
                show={!!notification}
                setShow={() => setNotification(null)}
                type={notification?.type || "success"}
                title={notification?.message || ""}
            />
        </PageView>
    )
}
